import React, { useState } from 'react';
import {
    CountyDropDown,
    DropDownBox,
    DropDownBoxComponentSpacing,
    DropDownBoxLayoutStyle,
    StateDropDown,
    YearDropDown,
} from '@apply/components';
import { Panel, PanelTheme } from '@apply/components';
import { DemoSection } from './common/SectionNavigator';
import { DemoPage, DemoPageSection } from './common/DemoLayout';

const sections: DemoSection[] = [
    { id: 'dropDownBoxDemo_dropdownbox', title: 'Drop Down Box' },
    { id: 'dropDownBoxDemo_dropdownboxhorizontal', title: 'Drop Down Box (Horizontal)' },
    { id: 'dropDownBoxDemo_inheritedcomponents', title: 'Inherited Components' },
    { id: 'dropDownBoxDemo_inheritedcomponentshorizontal', title: 'Inherited Components (Horizontal)' },
];

export const DropDownBoxDemo: React.FC = () => {
    // State for each example
    const [value, set_value] = useState('');
    const [advancedValue, set_advancedValue] = useState('');
    const [valueWithBubble, set_valueWithBubble] = useState('');
    const [horizontalValue, set_horizontalValue] = useState('');
    const [horizontalAdvancedValue, set_horizontalAdvancedValue] = useState('');
    const [horizontalValueWithBubble, set_horizontalValueWithBubble] = useState('');
    // State for the controls that inherit from DropDownBox
    const [stateValue, set_stateValue] = useState('');
    const [countyValue, set_countyValue] = useState('');
    const [yearValue, set_yearValue] = useState<number | undefined>();
    // Example dropdown items
    const dropdownItems = ['Option 1', 'Option 2', 'Option 3'];
    const dropdownItemsAdvancedDataBinding = [
        {
            id: 1,
            name: 'Property 1',
            price: 330,
        },
        {
            id: 2,
            name: 'Property 2',
            price: 400,
        },
        {
            id: 3,
            name: 'Property 3',
            price: 2400,
        },
    ];

    return (
        <DemoPage
            title='Drop Down Box :'
            sections={sections}
        >
            <DemoPageSection
                id={sections[0].id}
                title={sections[0].title}
            >
                <Panel theme={PanelTheme.White}>
                    <div style={{ padding: '1rem' }}>
                        {/* Example 1: Dropdown Box*/}
                        <div style={{ marginBottom: '1rem' }}>
                            <DropDownBox
                                id='dropdownboxDemo'
                                value={value}
                                onChange={set_value}
                                label='DropDownBox'
                                helperText='Helper Text: Click the arrow to view options'
                                required={true}
                                dataSource={dropdownItems}
                                placeholder='Select a demo item...'
                            />
                        </div>
                        {/* Example 2: Dropdown Box Read Only*/}
                        <div style={{ marginBottom: '1rem' }}>
                            <DropDownBox
                                id='dropdownboxDemo'
                                value={value}
                                onChange={set_value}
                                label='DropDownBox (Read Only)'
                                helperText='Helper Text: Click the arrow to view options'
                                required={true}
                                dataSource={dropdownItems}
                                placeholder='Select a demo item...'
                                readOnly={true}
                            />
                        </div>
                        {/* Example 2: Dropdown Box Advanced Binding*/}
                        <div style={{ marginBottom: '1rem' }}>
                            <DropDownBox
                                id='dropdownboxDemo'
                                value={advancedValue}
                                onChange={set_advancedValue}
                                label='DropDownBox (Advanced Data Binding)'
                                helperText='Helper Text: Click the arrow to view options'
                                required={true}
                                dataSource={dropdownItemsAdvancedDataBinding}
                                valueExpr='id'
                                displayExpr='name'
                                placeholder='Select an advanced demo item...'
                            />
                        </div>
                        {/* Example 4: Dropdown Box with Bubble*/}
                        <div style={{ marginBottom: '1rem' }}>
                            <DropDownBox
                                id='dropdownboxDemo'
                                value={valueWithBubble}
                                onChange={set_valueWithBubble}
                                label='DropDownBox with Information Bubble'
                                helperText='Helper Text: Click the arrow to view options'
                                required={true}
                                dataSource={dropdownItems}
                                placeholder='Select a demo item...'
                                showInfoIcon={true}
                                infoText='DropDownBox with Bubble'
                            />
                        </div>
                    </div>
                </Panel>
            </DemoPageSection>
            <DemoPageSection
                id={sections[1].id}
                title={sections[1].title}
            >
                <Panel theme={PanelTheme.White}>
                    <div style={{ padding: '1rem' }}>
                        {/* Example 5: Dropdown Box*/}
                        <div style={{ marginBottom: '1rem' }}>
                            <DropDownBox
                                id='dropdownboxDemo'
                                value={horizontalValue}
                                onChange={set_horizontalValue}
                                label='DropDownBox (Horiz.)'
                                helperText='Helper Text: Click the arrow to view options'
                                required={true}
                                dataSource={dropdownItems}
                                placeholder='Select a demo item...'
                                layoutStyle={DropDownBoxLayoutStyle.Horizontal}
                                labelWidth='auto'
                            />
                        </div>
                        {/* Example 6: Dropdown Box Read Only*/}
                        <div style={{ marginBottom: '1rem' }}>
                            <DropDownBox
                                id='dropdownboxDemo'
                                value={horizontalValue}
                                onChange={set_horizontalValue}
                                label='DropDownBox (Read Only) (Horiz.)'
                                helperText='Helper Text: Click the arrow to view options'
                                required={true}
                                dataSource={dropdownItems}
                                placeholder='Select a demo item...'
                                readOnly={true}
                                layoutStyle={DropDownBoxLayoutStyle.Horizontal}
                                labelWidth='auto'
                            />
                        </div>
                        {/* Example 7: Dropdown Box Advanced Binding*/}
                        <div style={{ marginBottom: '1rem' }}>
                            <DropDownBox
                                id='dropdownboxDemo'
                                value={horizontalAdvancedValue}
                                onChange={set_horizontalAdvancedValue}
                                label='DropDownBox (Advanced Data Binding) (Horiz.)'
                                helperText='Helper Text: Click the arrow to view options'
                                required={true}
                                dataSource={dropdownItemsAdvancedDataBinding}
                                valueExpr='id'
                                displayExpr='name'
                                placeholder='Select an advanced demo item...'
                                layoutStyle={DropDownBoxLayoutStyle.Horizontal}
                                labelWidth='auto'
                            />
                        </div>
                        {/* Example 8: Dropdown Box with Bubble*/}
                        <div style={{ marginBottom: '1rem' }}>
                            <DropDownBox
                                id='dropdownboxDemo'
                                value={horizontalValueWithBubble}
                                onChange={set_horizontalValueWithBubble}
                                label='DropDownBox with Information Bubble (Horiz.)'
                                helperText='Helper Text: Click the arrow to view options'
                                required={true}
                                dataSource={dropdownItems}
                                placeholder='Select a demo item...'
                                showInfoIcon={true}
                                infoText='DropDownBox with Bubble'
                                layoutStyle={DropDownBoxLayoutStyle.Horizontal}
                                labelWidth='auto'
                                width='auto'
                            />
                        </div>
                    </div>
                </Panel>
            </DemoPageSection>
            <DemoPageSection
                id={sections[2].id}
                title={sections[2].title}
            >
                <Panel theme={PanelTheme.White}>
                    <div style={{ padding: '1rem' }}>
                        {/* Example 9: State Dropdown */}
                        <div style={{ marginBottom: '1rem' }}>
                            <StateDropDown
                                id='stateDropDownDemo'
                                value={stateValue}
                                onChange={(state) => {
                                    // A county from the previous state no longer applies
                                    set_stateValue(state ?? '');
                                    set_countyValue('');
                                }}
                                label='State'
                                required={true}
                                placeholder='Select a state...'
                                componentSpacing={DropDownBoxComponentSpacing.StandardSpacing}
                            />
                        </div>
                        {/* Example 10: County Dropdown (filtered by the State selected above) */}
                        <div style={{ marginBottom: '1rem' }}>
                            <CountyDropDown
                                id='countyDropDownDemo'
                                value={countyValue}
                                selectedState={stateValue}
                                onChange={(county) => set_countyValue(county ?? '')}
                                label='County'
                                helperText='Helper Text: Lists the counties for the State selected above'
                                required={true}
                                placeholder={stateValue ? 'Select a county...' : 'Select a state first'}
                                readOnly={!stateValue}
                            />
                        </div>
                        {/* Example 11: Year Dropdown */}
                        <div style={{ marginBottom: '1rem' }}>
                            <YearDropDown
                                id='yearDropDownDemo'
                                value={yearValue}
                                // YearDropDown's onChange is typed as string, but it reports the selected year (a number) or null
                                onChange={(year) =>
                                    set_yearValue(year === null || year === undefined || year === '' ? undefined : Number(year))
                                }
                                label='Year'
                                required={true}
                                yearStart={1900}
                                placeholder='Select a year...'
                            />
                        </div>
                    </div>
                </Panel>
            </DemoPageSection>
            <DemoPageSection
                id={sections[3].id}
                title={sections[3].title}
            >
                <Panel theme={PanelTheme.White}>
                    <div style={{ padding: '1rem' }}>
                        {/* Example 12: State Dropdown (Horiz.) */}
                        <div style={{ marginBottom: '1rem' }}>
                            <StateDropDown
                                id='stateDropDownDemoHorizontal'
                                value={stateValue}
                                onChange={(state) => {
                                    // A county from the previous state no longer applies
                                    set_stateValue(state ?? '');
                                    set_countyValue('');
                                }}
                                label='State (Horiz.)'
                                required={true}
                                placeholder='Select a state...'
                                componentSpacing={DropDownBoxComponentSpacing.StandardSpacing}
                                layoutStyle={DropDownBoxLayoutStyle.Horizontal}
                                labelWidth='130px'
                            />
                        </div>
                        {/* Example 13: County Dropdown (Horiz.) (filtered by the State selected above) */}
                        <div style={{ marginBottom: '1rem' }}>
                            <CountyDropDown
                                id='countyDropDownDemoHorizontal'
                                value={countyValue}
                                selectedState={stateValue}
                                onChange={(county) => set_countyValue(county ?? '')}
                                label='County (Horiz.)'
                                helperText='Helper Text: Lists the counties for the State selected above'
                                required={true}
                                placeholder={stateValue ? 'Select a county...' : 'Select a state first'}
                                readOnly={!stateValue}
                                layoutStyle={DropDownBoxLayoutStyle.Horizontal}
                                labelWidth='130px'
                            />
                        </div>
                        {/* Example 14: Year Dropdown (Horiz.) */}
                        <div style={{ marginBottom: '1rem' }}>
                            <YearDropDown
                                id='yearDropDownDemoHorizontal'
                                value={yearValue}
                                // YearDropDown's onChange is typed as string, but it reports the selected year (a number) or null
                                onChange={(year) =>
                                    set_yearValue(year === null || year === undefined || year === '' ? undefined : Number(year))
                                }
                                label='Year (Horiz.)'
                                required={true}
                                yearStart={1900}
                                placeholder='Select a year...'
                                layoutStyle={DropDownBoxLayoutStyle.Horizontal}
                                labelWidth='130px'
                            />
                        </div>
                    </div>
                </Panel>
            </DemoPageSection>
        </DemoPage>
    );
};
